import styled from "styled-components";
import { colors } from "src/theme/colors";
import { radii, space, withAlpha } from "src/theme/ui";

/**
 * The card's body: as tall as its bands, and then as tall as the row.
 *
 * It was a fixed 248px, matched to the pane the composition cards gave
 * their ring, so all three cards in that row came out the same height
 * whatever they held — and four bands sat in the top of it with a hundred
 * pixels of paper underneath. Then it hugged its bands, which ended the
 * paper and left the row ragged.
 *
 * Now it asks for what its bands need, capped, and takes whatever the row
 * gives on top of that — the row equalises the cards, and the cards decide
 * what the row has to be. The bands and the scale under them are ONE block
 * centred in the result, so a card that is taller than its content is
 * taller above and below the block rather than under it: an axis a reader
 * has to look for at the far end of an empty card is not an axis.
 */
/* The card's own column: the bands take the room, the note takes the floor
   when there is any left over. */
export const Frame = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  /**
   * The bands and their scale sit a THIRD of the way down the body, which is
   * where every other short list in this product now sits: two weightless
   * spacers at one and two split whatever room is left over 1:2. Centred,
   * the block floated with nothing holding it to the heading; at the top it
   * read as a card that had been cut off.
   */
  &::before,
  &::after {
    content: "";
    flex: 1 1 0;
    min-height: 0;
  }

  &::after {
    flex-grow: 2;
  }
`;

/* One band, measured: a name line that comes out at 20px, 5px, an 8px
   track — and the 14px gap to the next one. Rounded UP, because a cap a
   fraction under its own content flies a scroll cue over a list that is
   complete.

   Seven of them, plus the room ScrollArea keeps for its cue. The number is
   not arbitrary: seven bands and the composition card's own cap of five
   legend rows come out within twenty pixels of each other, so the one row
   in this product where both cards are capped at once still reads level. */
const BAND = 34;
const BAND_GAP = 14;
const SCROLL_CUE = 22;
const MAX_BANDS = 7;
export const BANDS_CAP =
  MAX_BANDS * BAND + (MAX_BANDS - 1) * BAND_GAP + SCROLL_CUE;

/* What a given number of bands needs, scale included: the same arithmetic
   the cap is built from, so the card asking whether it has room to spare
   and the pane deciding when to scroll cannot disagree. The scale is its
   own 20px line plus the 6px above it. */
const SCALE = 26;
export const bandsHeight = (count: number) => {
  const shown = Math.min(Math.max(count, 1), MAX_BANDS);
  return shown * BAND + (shown - 1) * BAND_GAP + SCALE;
};

export const Bands = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/**
 * A band, and what it does under the pointer.
 *
 * These rows were inert: eleven ranges on one scale, and no way to tell
 * which figure belonged to which bar except by reading across a 400px gap.
 * On hover the band SWELLS and its fill fires — so the row being read is
 * unmistakable, and the eye can walk down the card comparing widths without
 * losing its place.
 *
 * A scale on the track, not a height: a height change reflows every row
 * below it, and the thickness of a bar carries no data here — the WIDTH and
 * the position do, and neither moves. Everything else is a fade.
 */
export const Band = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  /* The whole row is the target, including the words: pointing at the name
     lights its bar, which is the pairing a reader is trying to make. */
  cursor: default;
`;

export const BandHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space.md}px;
`;

export const Name = styled.span`
  font-size: 12.5px;
  color: ${colors.gray[700]};
  min-width: 0;
  transition: color 0.16s ease;

  ${Band}:hover & {
    color: ${colors.gray[900]};
  }
`;

export const Bounds = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${colors.gray[900]};
  transition: color 0.16s ease;

  /* The figures for the row under the pointer take the accent, so the pair
     — the bar and its two numbers, a hand's breadth apart — is read as one
     thing. */
  ${Band}:hover & {
    color: ${colors.primary[600]};
  }
`;

/* The common scale. Every band is measured against the same nought to a
   hundred, which is the only reason comparing them means anything. */
export const Track = styled.div`
  position: relative;
  height: 8px;
  border-radius: ${radii.pill}px;
  background: ${colors.gray[100]};
  overflow: hidden;
  transform-origin: center;
  transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.18s ease;

  ${Band}:hover & {
    transform: scaleY(1.375);
    background: ${colors.gray[200]};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background 0.18s ease;

    ${Band}:hover & {
      transform: none;
    }
  }
`;

/* The band itself: where it starts, and how much room it has. Not a bar
   from zero — a bar from zero would say the exposure IS that much, and what
   this says is that it may be anywhere in here. */
export const Fill = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: ${radii.pill}px;
  background: linear-gradient(
    to right,
    ${withAlpha(colors.primary[500], 0.55)},
    ${colors.primary[500]}
  );
  transition: background 0.18s ease;

  /* Solid under the pointer. The band's own colour is a fade from half
     strength — pretty across a column of them, and vague on the one you are
     actually reading, where both ends of the range want the same weight. */
  ${Band}:hover & {
    background: ${colors.primary[500]};
  }
`;

export const Scale = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;

  span {
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${colors.gray[400]};
    font-variant-numeric: tabular-nums;
  }
`;
